// ./src/components/features/Rentals/rentalsHelpers.ts
//
// Timezone helpers for a rental's pickup / drop-off ends. Each end stores a TRUE
// UTC instant (pickupAt/dropoffAt) paired with its own IANA zone (pickupTimezone/
// dropoffTimezone, auto-resolved server-side). These convert the stored UTC back
// to that end's local wall-clock for DISPLAY and for the edit picker, so a rental
// always reads in the location's own time — the Travel-module rule.

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

const PICKER_FORMAT = "YYYY-MM-DD HH:mm:ss";

/** A UTC instant → the naive wall-clock string (in `tz`) the DateTimeField binds to. */
export const utcToLocalInput = (iso?: string | null, tz?: string | null): string | null => {
  if (!iso) {
    return null;
  }
  const d = dayjs.utc(iso);
  if (!d.isValid()) {
    return null;
  }
  return tz ? d.tz(tz).format(PICKER_FORMAT) : d.format(PICKER_FORMAT);
};

/** A short zone abbreviation (e.g. "PDT") for an instant, or "" when unknown. */
export const zoneAbbreviation = (iso?: string | null, tz?: string | null, locale = "en"): string => {
  if (!iso || !tz) {
    return "";
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  try {
    const parts = new Intl.DateTimeFormat(locale, { hour: "2-digit", timeZoneName: "short", timeZone: tz }).formatToParts(date);
    return parts.find((p) => p.type === "timeZoneName")?.value ?? "";
  } catch {
    return "";
  }
};

/** A rental end's when, rendered in its own zone, e.g. "Sep 1, 10:00 PDT". Empty when no time. */
export const formatRentalWhen = (iso?: string | null, tz?: string | null, locale = "en"): string => {
  if (!iso) {
    return "";
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  try {
    const main = new Intl.DateTimeFormat(locale, {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: tz || undefined,
    }).format(date);
    const abbr = zoneAbbreviation(iso, tz, locale);
    return abbr ? `${main} ${abbr}` : main;
  } catch {
    return "";
  }
};
